/**
 * Importing `legacy_automations` into our recipe model (`specs/M6-A1-automation-engine.md` §9).
 *
 * Two paths, because monday gives us two very different inputs:
 *
 * - **`custom` / `app_recipe`** (176 of 226 definitions): the recipe definition carries
 *   `rawNodes`, so the chain is walked from `isRootNode` through `nextNodesConfig.nextNodeId`
 *   and the config slots are keyed by node id. Nothing is guessed.
 * - **`static` / `oneOff`** (50 definitions, 230 automations): monday returns **no** `rawNodes`,
 *   so the composition comes from the table below. It used to be inferred from the sentence
 *   text; it is now taken from monday's own catalogue, where a new-builder recipe names the
 *   static recipe it replaces in `overrideStaticRecipeId`
 *   (`discovery/automations/api/static-recipe-mapping.md`, sandbox E14). Entries that still have
 *   no counterpart there — `293`, `122`, `160`, `24`, `151`, `169`, the webhook family — are
 *   marked `assumed` and use `ez.*` local blocks.
 *
 * **R7: nothing is ever dropped.** A recipe the registry cannot build imports with
 * `mappingStatus: 'unmapped'`, `active: false`, its `legacy_config` intact and a notice, so it
 * shows up in the automations centre greyed instead of vanishing. `partial` is the same for a
 * recipe whose trigger maps but one action does not.
 */
import { automationBlockRegistry, isActionBlock, isConditionBlock, isTriggerBlock } from './block.js';
import { automationRecipeSchema } from './recipe.js';
import { AUTOMATION_NOTICES } from './states.js';
import { ANY_VALUE } from './values.js';
/**
 * The `webhooks` static recipes: twelve recipes, 42 automations, one action. `eventName` is
 * monday's own webhook event name for the same trigger (`discovery/api-corpus/webhooks.md`
 * §7), so a consumer that routed on it keeps working after the migration.
 */
const WEBHOOK_URL_NOT_EXPORTED = {
    code: 'webhook_url_not_exported',
    notice: AUTOMATION_NOTICES.webhookUrlMissing,
};
const webhookRecipe = (trigger, eventName) => ({
    assumed: true,
    incomplete: WEBHOOK_URL_NOT_EXPORTED,
    nodes: [trigger, { block: 'ez.a.send_webhook', slots: {}, fixed: { eventName } }],
});
const CONFIRMED = 'overrideStaticRecipeId in /automations-ms/recipes';
/**
 * `static` recipe id → block chain. Ordered by usage on this account (`specs/M6-A3` §14).
 * Slot names are monday's own, read from `configurationFields` in the captured catalogue, so a
 * mis-typed slot name is a test failure rather than a silent no-op.
 */
export const STATIC_RECIPE_MAPPINGS = {
    // ── status trigger ───────────────────────────────────────────────────────
    16: { source: CONFIRMED, nodes: [
            { block: '223025', slots: { columnId: 'columnId', columnValue: 'statusColumnValue' } },
            { block: '40', slots: { groupId: 'groupId' } },
        ] },
    19: { source: CONFIRMED, nodes: [
            { block: '223025', slots: { columnId: 'columnId', columnValue: 'statusColumnValue' } },
            { block: '5200', slots: { message: 'message', receiver: 'receiver' } },
        ] },
    15: { source: CONFIRMED, nodes: [
            { block: '223025', slots: { columnId: 'columnId', columnValue: 'columnValue' } },
            { block: '42429', slots: {} },
        ] },
    17: { source: CONFIRMED, nodes: [
            { block: '223025', slots: { columnId: 'columnId', columnValue: 'columnValue' } },
            { block: '42432', slots: {} },
        ] },
    102: { source: CONFIRMED, nodes: [
            { block: '223025', slots: { statusColumn: 'columnId', columnValue: 'statusColumnValue' } },
            { block: '50', slots: { user: 'columnValue', personColumn: 'columnId' } },
        ] },
    144: { source: CONFIRMED, nodes: [
            { block: '223025', slots: { statusColumnId: 'columnId', statusColumnValue: 'statusColumnValue' } },
            { block: '468', slots: { dueDateColumnId: 'dateColumnId', duration: 'duration' } },
        ] },
    220: { source: CONFIRMED, nodes: [
            { block: '223025', slots: { columnId: 'columnId', columnValue: 'columnValue' } },
            { block: '1838', slots: { statusColumnId: 'columnId', isCondition: 'isCondition', statusColumnValue: 'columnValue' } },
            { block: '40', slots: { groupId: 'groupId' } },
        ] },
    277: { source: CONFIRMED, nodes: [
            { block: '223025', slots: { columnId: 'columnId', columnValue: 'columnValue' } },
            { block: '1838', slots: { conditionColumnId: 'columnId', isCondition: 'isCondition', conditionColumnValue: 'columnValue' } },
            { block: '41', slots: { toColumnId: 'columnId', toColumnValue: 'statusColumnValue' } },
        ] },
    280: { assumed: true, nodes: [
            { block: '223025', slots: { columnId: 'columnId', columnValue: 'statusColumnValue' } },
            { block: '41', slots: { anotherColumnId: 'columnId', anotherColumnValue: 'statusColumnValue' } },
        ] },
    281: { assumed: true, nodes: [
            { block: '223025', slots: { statusColumnId: 'columnId', statusColumnValue: 'statusColumnValue' } },
            { block: '380', slots: { dueDateColumnId: 'columnId' } },
            { block: '468', slots: { dueDateColumnId: 'dateColumnId', duration: 'duration' } },
        ] },
    298: { source: CONFIRMED, nodes: [
            { block: '223025', slots: { statusColumnId: 'columnId', statusColumnValue: 'statusColumnValue' } },
            { block: '5941', slots: { message: 'updateBody' } },
        ] },
    169: { assumed: true, nodes: [
            { block: '223025', slots: { columnId: 'columnId', columnValue: 'statusColumnValue' } },
            { block: '40', slots: { groupId: 'groupId' }, delaySlot: 'duration' },
        ] },
    293: { assumed: true, nodes: [
            { block: '223025', slots: { statusColumnId: 'columnId' }, fixed: { statusColumnValue: { $any$: true } } },
            { block: 'ez.a.create_premade_update', slots: { statusColumnId: 'statusColumnId' } },
        ] },
    131: { assumed: true, nodes: [
            { block: '223089', slots: { columnId: 'columnId', fromColumnValue: 'previousStatusColumnValue', toColumnValue: 'statusColumnValue' } },
            { block: '5200', slots: { message: 'message', receiver: 'receiver' } },
        ] },
    // ── item created ─────────────────────────────────────────────────────────
    20: { source: CONFIRMED, nodes: [
            { block: '55', slots: {} },
            { block: '5200', slots: { message: 'message', receiver: 'receiver' } },
        ] },
    104: { source: CONFIRMED, nodes: [
            { block: '55', slots: {} },
            { block: '41', slots: { columnId: 'columnId', columnValue: 'statusColumnValue' } },
        ] },
    106: { source: CONFIRMED, nodes: [
            { block: '55', slots: {} },
            { block: '1822', slots: { columnId: 'columnId' } },
        ] },
    109: { source: CONFIRMED, nodes: [
            { block: '55', slots: {} },
            { block: '50', slots: { user: 'columnValue', columnId: 'columnId' } },
        ] },
    279: { source: CONFIRMED, nodes: [
            { block: '55', slots: {} },
            { block: '380', slots: { dueDateColumnId: 'columnId' } },
            { block: '468', slots: { dueDateColumnId: 'dateColumnId', duration: 'duration' } },
        ] },
    151: { assumed: true, nodes: [
            { block: 'ez.t.item_created_in_group', slots: { groupId: 'groupId' } },
            { block: '5200', slots: { message: 'message', receiver: 'receiver' } },
        ] },
    // ── date arrives ─────────────────────────────────────────────────────────
    29: { source: CONFIRMED, nodes: [
            { block: '580', slots: { dateTrigger: 'dateTriggerConfig', dueDateColumn: 'columnId' } },
            { block: '5100', slots: { message: 'message', receiver: 'receiver' } },
        ] },
    32: { source: CONFIRMED, nodes: [
            { block: '580', slots: { dateTrigger: 'dateTriggerConfig', dueDateColumn: 'columnId' } },
            { block: '1838', slots: { columnId: 'columnId', isCondition: 'isCondition', columnValue: 'columnValue' } },
            { block: '5100', slots: { message: 'message', receiver: 'receiver' } },
        ] },
    140: { source: CONFIRMED, nodes: [
            { block: '580', slots: { dateTrigger: 'dateTriggerConfig', dueDateColumn: 'columnId' } },
            { block: '40', slots: { groupId: 'groupId' } },
        ] },
    142: { source: CONFIRMED, nodes: [
            { block: '580', slots: { dateTrigger: 'dateTriggerConfig', dueDateColumn: 'columnId' } },
            { block: '1838', slots: { columnId: 'columnId', isCondition: 'isCondition', columnValue: 'columnValue' } },
            { block: '40', slots: { groupId: 'groupId' } },
        ] },
    145: { source: CONFIRMED, nodes: [
            { block: '580', slots: { dateTrigger: 'dateTriggerConfig', dueDateColumn: 'columnId' } },
            { block: '1838', slots: { columnId: 'columnId', isCondition: 'isCondition', columnValue: 'columnValue' } },
            { block: '41', slots: { columnId: 'columnId', toColumnValue: 'statusColumnValue' } },
        ] },
    200: { source: CONFIRMED, nodes: [
            { block: '580', slots: { dateTrigger: 'dateTriggerConfig', dateColumnId: 'columnId' } },
            { block: '42429', slots: {} },
        ] },
    138: { source: CONFIRMED, nodes: [
            { block: '580', slots: { dateTrigger: 'dateTriggerConfig', dateColumnId: 'columnId' } },
            { block: '40', slots: { groupId: 'groupId' } },
            { block: '468', slots: { dateColumnId: 'dateColumnId', duration: 'duration' } },
        ] },
    // ── recurrence ───────────────────────────────────────────────────────────
    133: { source: CONFIRMED, nodes: [
            { block: '47', slots: { schedulerConfig: 'schedulerConfig' } },
            { block: '507', slots: { pulseMapping: 'itemColumnValues' } },
        ] },
    152: { source: CONFIRMED, nodes: [
            { block: '47', slots: { schedulerConfig: 'schedulerConfig' } },
            { block: '1825', slots: { groupId: 'groupId' } },
        ] },
    170: { assumed: true, nodes: [
            { block: '47', slots: { schedulerConfig: 'schedulerConfig' } },
            { block: '5200', slots: { message: 'message', receiver: 'receiver' } },
        ] },
    // ── column / move / update / name ────────────────────────────────────────
    18: { source: CONFIRMED, nodes: [
            { block: '54', slots: { columnId: 'columnId' } },
            { block: '5200', slots: { message: 'message', receiver: 'receiver' } },
        ] },
    24: { assumed: true, nodes: [
            { block: 'ez.t.item_moved_to_group', slots: { groupId: 'groupId' } },
            { block: '5200', slots: { message: 'message', receiver: 'receiver' }, delaySlot: 'duration' },
        ] },
    286: { assumed: true, nodes: [
            { block: 'ez.t.update_created', slots: {} },
            { block: '5200', slots: { message: 'message', receiver: 'receiver' } },
        ] },
    // ── M6-A4 · cross-board creators (chains CONFIRMED by `overrideStaticRecipeId`) ──────────
    111: { source: CONFIRMED, nodes: [
            { block: '223025', slots: { columnId: 'columnId', columnValue: 'statusColumnValue' } },
            { block: '5826', slots: { pulseMapping: 'itemMapping', targetBoardId: 'targetBoardId', sourceBoardId: 'sourceBoardId' }, requiredSlots: ['targetBoardId'] },
        ] },
    183: { source: CONFIRMED, nodes: [
            { block: '223025', slots: { columnId: 'columnId', columnValue: 'statusColumnValue' } },
            { block: '5826', slots: { pulseMapping: 'itemMapping', targetBoardId: 'targetBoardId', sourceBoardId: 'sourceBoardId' }, requiredSlots: ['targetBoardId'] },
        ] },
    184: { source: CONFIRMED, nodes: [
            { block: '580', slots: { dateTrigger: 'dateTriggerConfig', dueDateColumn: 'columnId' } },
            { block: '5826', slots: { pulseMapping: 'itemMapping', targetBoardId: 'targetBoardId', sourceBoardId: 'sourceBoardId' }, requiredSlots: ['targetBoardId'] },
        ] },
    180: { source: CONFIRMED, nodes: [
            { block: '223025', slots: { columnId: 'columnId', columnValue: 'statusColumnValue' } },
            { block: '6899', slots: { pulseMapping: 'itemMapping', targetBoardId: 'targetBoardId' }, requiredSlots: ['targetBoardId'] },
        ] },
    181: { source: CONFIRMED, nodes: [
            { block: '580', slots: { dateTrigger: 'dateTriggerConfig', dueDateColumn: 'columnId' } },
            { block: '6899', slots: { pulseMapping: 'itemMapping', targetBoardId: 'targetBoardId' }, requiredSlots: ['targetBoardId'] },
        ] },
    222: { source: CONFIRMED, nodes: [
            { block: '55', slots: {} },
            { block: '15499', slots: { pulseMapping: 'itemMapping', targetBoardId: 'targetBoardId', linkToPulseColumnId: 'linkageColumn', sourceBoardId: 'sourceBoardId' }, requiredSlots: ['targetBoardId'] },
        ] },
    226: { source: CONFIRMED, nodes: [
            { block: '223025', slots: { columnId: 'columnId', columnValue: 'statusColumnValue' } },
            { block: '1838', slots: { conditionColumnId: 'columnId', isCondition: 'isCondition', conditionColumnValue: 'columnValue' } },
            { block: '15499', slots: { pulseMapping: 'itemMapping', targetBoardId: 'targetBoardId', linkToPulseColumnId: 'linkageColumn', sourceBoardId: 'sourceBoardId' }, requiredSlots: ['targetBoardId'] },
        ] },
    /**
     * `225` and `223` are the same sentence with the connect column on **opposite** boards, and
     * monday says which in its own `configurationFields`: `225` declares
     * `linkToPulseColumnId.linkedToBoard = "sourceBoardId"` with `boardIdField = "targetBoardId"`
     * (the column lives on the target board), `223` declares `linkedToBoard = "targetBoardId"`
     * (it lives on this board). `derive` is what carries that distinction into the column ref.
     */
    225: { source: CONFIRMED, nodes: [
            { block: '223025', slots: { columnId: 'columnId', columnValue: 'statusColumnValue' } },
            {
                block: '15499',
                slots: { pulseMapping: 'itemMapping', targetBoardId: 'targetBoardId', sourceBoardId: 'sourceBoardId' },
                requiredSlots: ['targetBoardId'],
                derive: (config) => typeof config.linkToPulseColumnId === 'string'
                    ? { linkageColumn: { columnId: config.linkToPulseColumnId, boardId: config.targetBoardId ?? undefined } }
                    : {},
            },
        ] },
    223: { assumed: true, nodes: [
            { block: '223025', slots: { columnId: 'columnId', columnValue: 'statusColumnValue' } },
            {
                block: '15499',
                slots: { pulseMapping: 'itemMapping', targetBoardId: 'targetBoardId', sourceBoardId: 'sourceBoardId' },
                requiredSlots: ['targetBoardId'],
                derive: (config) => typeof config.linkToPulseColumnId === 'string' ? { linkageColumn: { columnId: config.linkToPulseColumnId } } : {},
            },
        ] },
    // ── M6-A4 · Slack (`configurationFields`: `slackChannel`/`slackUser` are remote lists) ────
    38: { assumed: true, nodes: [
            { block: '55', slots: {} },
            { block: '7459', slots: { message: 'message', slackChannel: 'slackChannel' }, requiredSlots: ['slackChannel'] },
        ] },
    40: { assumed: true, nodes: [
            { block: '223025', slots: { columnId: 'columnId', columnValue: 'statusColumnValue' } },
            { block: '7459', slots: { message: 'message', slackChannel: 'slackChannel' }, requiredSlots: ['slackChannel'] },
        ] },
    96: { assumed: true, nodes: [
            { block: '54', slots: { columnId: 'columnId' } },
            { block: '7459', slots: { message: 'message', slackChannel: 'slackChannel' }, requiredSlots: ['slackChannel'] },
        ] },
    98: { assumed: true, nodes: [
            { block: 'ez.t.update_created', slots: {} },
            {
                block: 'ez.a.slack_send_update',
                slots: { slackChannel: 'slackChannel' },
                // The sentence is "send **it**": the update itself is the message, and the trigger
                // publishes it as `{update.preview}` (sandbox E14, recipe 286's own extra token).
                fixed: { message: '{update.preview}' },
                requiredSlots: ['slackChannel'],
            },
        ] },
    801: { assumed: true, nodes: [
            { block: '55', slots: {} },
            { block: 'ez.a.slack_notify_user', slots: { message: 'message', slackUser: 'slackUser' }, requiredSlots: ['slackUser'] },
        ] },
    802: { assumed: true, nodes: [
            { block: '580', slots: { dateTrigger: 'dateTriggerConfig', dueDateColumn: 'columnId' } },
            { block: 'ez.a.slack_notify_user', slots: { message: 'message', slackUser: 'slackUser' }, requiredSlots: ['slackUser'] },
        ] },
    // ── M6-A4 · Gmail and Google Calendar ────────────────────────────────────────────────────
    174: { assumed: true, nodes: [
            { block: '47', slots: { schedulerConfig: 'schedulerConfig' } },
            { block: '1838', slots: { statusColumnId: 'columnId', isCondition: 'isCondition', statusColumnValue: 'columnValue' } },
            { block: 'ez.a.send_email', slots: { email: 'email', receiver: 'receiver' }, requiredSlots: ['email', 'receiver'] },
        ] },
    6001: { assumed: true, nodes: [
            { block: 'ez.t.item_created_or_updated', slots: {} },
            { block: 'ez.a.create_calendar_event', slots: { eventMapping: 'eventMapping', sourceBoardId: 'sourceBoardId' }, requiredSlots: ['eventMapping'] },
        ] },
    // ── M6-A4 · the twelve `webhooks` recipes (42 automations, URL not exportable) ────────────
    127: webhookRecipe({ block: '54', slots: {}, fixed: { columnId: ANY_VALUE } }, 'change_column_value'),
    1004: webhookRecipe({ block: 'ez.t.item_name_changed', slots: {} }, 'change_name'),
    128: webhookRecipe({ block: 'ez.t.update_created', slots: {} }, 'create_update'),
    7011: webhookRecipe({ block: 'ez.t.update_changed', slots: {} }, 'edit_update'),
    7012: webhookRecipe({ block: 'ez.t.update_deleted', slots: {} }, 'delete_update'),
    7004: webhookRecipe({ block: 'ez.t.item_deleted', slots: {} }, 'item_deleted'),
    193: webhookRecipe({ block: 'ez.t.item_moved_to_group', slots: {}, fixed: { groupId: ANY_VALUE } }, 'item_moved_to_any_group'),
    7000: webhookRecipe({ block: 'ez.t.subitem_created', slots: {} }, 'create_subitem'),
    7001: webhookRecipe({ block: 'ez.t.subitem_update_created', slots: {} }, 'create_subitem_update'),
    7002: webhookRecipe({ block: 'ez.t.subitem_column_changed', slots: {} }, 'change_subitem_column_value'),
    7003: webhookRecipe({ block: 'ez.t.subitem_name_changed', slots: {} }, 'change_subitem_name'),
    7005: webhookRecipe({ block: 'ez.t.subitem_deleted', slots: {} }, 'subitem_deleted'),
    /**
     * `3000` — monday's *One Off Automation* (18 rows): a snooze reminder, not a recipe. Its
     * config is a shape of its own and all 18 rows are uniform — one `system:cron` listener and
     * exactly one action, `notifyUsers` with four positional params
     * `[boardId, message, receiver, options]`. `derive` reads that shape; anything else returns
     * `null` and the row imports `partial` rather than being mis-read.
     */
    3000: { assumed: true, nodes: [
            { block: 'ez.t.cron_oneoff', slots: { listeners: 'listeners', resourceMetadata: 'resourceMetadata' } },
            {
                block: '5200',
                slots: {},
                derive: (config) => {
                    const actions = config.actions;
                    if (!Array.isArray(actions) || actions.length === 0)
                        return null;
                    const first = actions[0];
                    if (first.actionName !== 'notifyUsers' || !Array.isArray(first.actionParams))
                        return null;
                    const [, message, receiver] = first.actionParams;
                    if (typeof message !== 'string' || typeof receiver !== 'object' || receiver === null)
                        return null;
                    return { message, receiver };
                },
            },
        ] },
};
/**
 * A node's role: the **registry** decides it for a block we implement, the `sentencePartial`
 * only for one we do not (`specs/M6-A3` §9).
 *
 * Reading the partial alone is what P-298 was. `rawNodes` does not always repeat the partial on
 * the node — recipe `13964` (automation 11843871) keeps its partials in the recipe's
 * `sentenceParts` and leaves node 2 (block `45`) with none — so the heuristic saw no
 * "and only if / to / from", filed the condition as an **action**, and the engine skipped the
 * run with `block_unavailable` instead of moving the item. 6 of the 17 `45` recipes are wired
 * that way; across the whole corpus no registered block's partial disagrees with its own
 * registration, so preferring the registry changes those 6 and nothing else.
 *
 * A registered **trigger** reached here is not the root node the graph named, so it is left to
 * the partial rather than allowed to overwrite `recipe.trigger`.
 */
export function nodeKindOf(node) {
    if (node.isRootNode === true)
        return 'trigger';
    const registered = node.workflowBlockId === undefined ? undefined : automationBlockRegistry.find(String(node.workflowBlockId));
    if (registered !== undefined && registered.kind !== 'trigger')
        return registered.kind;
    const partial = (node.sentencePartial ?? '').trimStart().toLowerCase();
    if (partial.startsWith('and only if') || partial.startsWith('to ') || partial.startsWith('from '))
        return 'condition';
    return 'action';
}
/** `nodeInboundFieldsSourceConfig` → our normalised `inputs` (§4.2). */
export function normaliseInputs(raw) {
    const inputs = {};
    for (const [key, field] of Object.entries(raw ?? {})) {
        const fieldKey = field.fieldKey ?? key;
        const dependents = field.sourceMetadata?.dependentNodesFieldConfig;
        if (dependents !== undefined && dependents !== null) {
            const [nodeId, nodeField] = Object.entries(dependents)[0] ?? [];
            if (nodeId !== undefined && nodeField !== undefined) {
                inputs[key] = { from: 'node', nodeId, field: nodeField };
                continue;
            }
        }
        if (field.sourceType === 'workflow_execution_context') {
            inputs[key] = { from: 'context', field: field.sourceMetadata?.contextFieldKey ?? fieldKey };
            continue;
        }
        inputs[key] = { from: 'config', field: fieldKey };
    }
    return inputs;
}
/**
 * Maps one `legacy_automations` row. `definition` is monday's recipe definition when it returned
 * one; a `static` recipe has none and goes through `STATIC_RECIPE_MAPPINGS`.
 */
export function mapLegacyAutomation(row, definition) {
    const config = row.config ?? {};
    const rawNodes = definition?.rawNodes?.nodes;
    return rawNodes !== undefined && rawNodes !== null && rawNodes.length > 0
        ? mapFromRawNodes(rawNodes, config)
        : mapFromStaticTable(String(row.recipeId), config);
}
function mapFromRawNodes(nodes, config) {
    const byId = new Map(nodes.map((node) => [String(node.id), node]));
    const root = nodes.find((node) => node.isRootNode === true) ?? nodes[0];
    if (root === undefined)
        return unmapped([{ code: 'no_trigger', detail: 'rawNodes is empty' }]);
    const issues = [];
    const chain = [];
    const seen = new Set();
    let cursor = root;
    while (cursor !== undefined) {
        if (seen.has(String(cursor.id))) {
            issues.push({ code: 'non_linear', detail: `node ${cursor.id} repeats` });
            break;
        }
        seen.add(String(cursor.id));
        chain.push(cursor);
        const next = cursor.nextNodesConfig?.nextNodeId;
        cursor = next === undefined || next === null ? undefined : byId.get(String(next));
    }
    const recipe = { conditions: [], actions: [] };
    /** A `sentence_text_term` node carries no behaviour, but one recipe stores a `message` in its
     *  slot; that value is attached to the **following** action rather than discarded (§9). */
    let carried = {};
    let unknownBlocks = 0;
    for (const node of chain) {
        const slot = (config[String(node.id)] ?? {});
        if (node.type === 'sentence_text_term' || node.workflowBlockId === undefined) {
            carried = { ...carried, ...slot };
            continue;
        }
        const block = String(node.workflowBlockId);
        const params = { ...carried, ...slot };
        carried = {};
        if (!automationBlockRegistry.has(block)) {
            unknownBlocks += 1;
            issues.push({ code: 'unknown_block', detail: `block ${block} is not implemented`, nodeId: String(node.id) });
            continue;
        }
        const entry = { nodeId: String(node.id), block, params, inputs: normaliseInputs(node.nodeInboundFieldsSourceConfig) };
        const kind = nodeKindOf(node);
        if (kind === 'trigger')
            recipe.trigger = entry;
        else if (kind === 'condition')
            recipe.conditions.push(entry);
        else
            recipe.actions.push({ ...entry, delay: null });
    }
    if (recipe.trigger === undefined) {
        return unmapped(issues.length > 0 ? issues : [{ code: 'no_trigger', detail: 'no root node resolved' }]);
    }
    const parsed = automationRecipeSchema.safeParse(recipe);
    if (!parsed.success)
        return unmapped([...issues, { code: 'invalid_params', detail: parsed.error.message }]);
    return finish(parsed.data, issues, unknownBlocks);
}
function mapFromStaticTable(recipeId, config) {
    const mapping = STATIC_RECIPE_MAPPINGS[recipeId];
    if (mapping === undefined) {
        return unmapped([{ code: 'no_definition', detail: `static recipe ${recipeId} has no mapping` }]);
    }
    const issues = [];
    let unknownBlocks = 0;
    let deriveFailed = false;
    const recipe = { conditions: [], actions: [] };
    mapping.nodes.forEach((node, index) => {
        const definition = automationBlockRegistry.find(node.block);
        if (definition === undefined) {
            unknownBlocks += 1;
            issues.push({ code: 'unknown_block', detail: `block ${node.block} is not implemented` });
            return;
        }
        const params = { ...node.fixed };
        for (const [slot, param] of Object.entries(node.slots)) {
            if (config[slot] !== undefined)
                params[param] = config[slot];
        }
        if (node.derive !== undefined) {
            const derived = node.derive(config);
            if (derived === null) {
                deriveFailed = true;
                issues.push({ code: 'invalid_params', detail: `node ${index} of recipe ${recipeId} has an unreadable config` });
                return;
            }
            Object.assign(params, derived);
        }
        // A required slot that monday itself left empty: the recipe is representable but cannot run.
        for (const slot of node.requiredSlots ?? []) {
            const value = config[slot];
            if (value === undefined || value === null) {
                issues.push({ code: 'slot_not_configured', detail: `slot "${slot}" is empty`, nodeId: `s${index}` });
            }
        }
        const entry = { nodeId: `s${index}`, block: node.block, params, inputs: {} };
        if (isTriggerBlock(definition))
            recipe.trigger = entry;
        else if (isConditionBlock(definition))
            recipe.conditions.push(entry);
        else if (isActionBlock(definition)) {
            const delay = node.delaySlot === undefined ? null : (config[node.delaySlot] ?? null);
            recipe.actions.push({ ...entry, delay });
        }
    });
    if (recipe.trigger === undefined)
        return unmapped([...issues, { code: 'no_trigger', detail: `recipe ${recipeId}` }]);
    if (deriveFailed)
        return unmapped(issues);
    if (mapping.incomplete !== undefined) {
        issues.push({ code: 'not_exportable', detail: mapping.incomplete.code });
    }
    const parsed = automationRecipeSchema.safeParse(recipe);
    if (!parsed.success)
        return unmapped([...issues, { code: 'invalid_params', detail: parsed.error.message }]);
    return finish(parsed.data, issues, unknownBlocks, mapping.incomplete?.notice);
}
function finish(recipe, issues, unknownBlocks, incompleteNotice) {
    if (unknownBlocks > 0) {
        // R7: the mapped part is kept and the automation stays off until a human resolves it.
        return { recipe, status: 'partial', issues, noticeMessage: AUTOMATION_NOTICES.partialRecipe };
    }
    if (incompleteNotice !== undefined) {
        // The chain is complete; one value monday never exported is not. Same handling as a
        // partial (kept, off, annotated) with a notice that says exactly what a human must supply.
        return { recipe, status: 'partial', issues, noticeMessage: incompleteNotice };
    }
    if (issues.some((issue) => issue.code === 'slot_not_configured')) {
        return { recipe, status: 'partial', issues, noticeMessage: AUTOMATION_NOTICES.slotNotConfigured };
    }
    return { recipe, status: 'mapped', issues, noticeMessage: null };
}
function unmapped(issues) {
    return { recipe: null, status: 'unmapped', issues, noticeMessage: AUTOMATION_NOTICES.unmappedRecipe };
}
//# sourceMappingURL=mapping.js.map